import { useRef } from 'react'
import type ReactECharts from 'echarts-for-react'
import { useRiskFilters } from '../../../../shared/hooks/useRiskFilters'
import { useElementGroupBuckets } from '../../../hooks/useElementGroupBuckets'
import { RiskProfileCard } from '../../RiskProfileCard'
import { ChartQueryState } from '../../ChartQueryState'
import { useExportXlsx } from '../../../hooks/useExportXlsx'
import RiskByElementsChart from '../../../chart/RiskByElements.chart'
import { useProfileElementGroupData } from '../../../hooks/useProfileElementGroupData'
import type { RiskProfileQueryParams } from '../../../hooks/useRiskProfileQueryParams'
import type { RiskProfileMetricOption } from '../../../types/riskProfile.types'
import { ChartExportActions } from '../../../chart/ChartExportActions'
import { useExportChartImage } from '../../../hooks/useExportChartImage'

export interface RiskByElementGroupWidgetProps {
  queryParams: RiskProfileQueryParams | null
  metricOption: RiskProfileMetricOption
}

export function RiskByElementGroupWidget({
  queryParams,
  metricOption,
}: RiskByElementGroupWidgetProps) {
  const { setFilters, setAnalysisMode } = useRiskFilters()
  const { request, query } = useProfileElementGroupData(queryParams)
  const { exportXlsx, isExporting } = useExportXlsx(request)
  const elementGroupBuckets = useElementGroupBuckets(queryParams?.assets ?? [])
  const chartRef = useRef<ReactECharts>(null)

  const handleGroupClick = (groupLabel: string) => {
    const bucket = elementGroupBuckets.find((b) => Object.keys(b)[0] === groupLabel)
    const memberIds = bucket ? Object.values(bucket)[0] : []
    setFilters({ elementGroups: memberIds })
    setAnalysisMode('by-type')
  }
  const handleExportImage = useExportChartImage(chartRef, 'risk-by-element-group')

  return (
    <RiskProfileCard
      title="Risk by element group"
      description="Ranked exposure by element group for the selected hazards and metric."
      onExport={
        <ChartExportActions
          onExportXlsx={exportXlsx}
          onExportImage={handleExportImage}
          isExportingXlsx={isExporting}
          xlsxDisabled={!request}
        />
      }
      className="min-h-96"
    >
      <ChartQueryState query={query}>
        {(data) => (
          <RiskByElementsChart
            ref={chartRef}
            data={data}
            metricOption={metricOption}
            onItemClick={handleGroupClick}
          />
        )}
      </ChartQueryState>
    </RiskProfileCard>
  )
}
